import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/site/Reveal";
import { useSeo } from "../lib/seo";
import { CONTACT } from "../lib/site";
import { LEGAL } from "../constants/testIds";

export default function Privacy() {
  useSeo({
    title: "Privacy Policy | Alae Dental",
    description:
      "How Alae Dental collects, uses, and protects your personal information.",
    path: "/privacy",
  });

  return (
    <main data-testid={LEGAL.privacyPage} className="bg-linen text-umber">
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-paper paper-grain">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-meta text-forest mb-6">Legal</div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display text-5xl md:text-6xl leading-[1.04] tracking-tight text-umber">
              Privacy Policy
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 font-meta text-umber/70">
              Last updated: {new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 paper-grain">
        <div className="max-w-3xl mx-auto px-6 md:px-10 prose-alae">
          <Reveal>
            <div data-testid={LEGAL.notice} className="rounded-sm border border-forest/25 bg-paper/60 p-5 mb-10 text-sm text-umber/85 leading-relaxed">
              <strong>Placeholder notice.</strong> This document is a working
              draft intended as a starting point. It should be reviewed and
              adapted by legal counsel familiar with Ontario and Canadian
              privacy law (PIPEDA and PHIPA) before the site launches publicly.
            </div>
          </Reveal>

          <Reveal delay={80}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">1. Who we are</h2>
            <p>
              Alae Dental (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) is a dental practice
              located at {CONTACT.address}. This Privacy Policy explains how we
              collect, use, disclose, and protect the personal information you
              share with us through this website and our practice.
            </p>
          </Reveal>

          <Reveal delay={100}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">2. Information we collect</h2>
            <p>Through this website we may collect:</p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-umber/85">
              <li>
                <strong>Contact and appointment details</strong> you give us
                when you request a callback or book an appointment: your name,
                email address, phone number, preferred contact method,
                preferred timing, and any services you note interest in.
              </li>
              <li>
                <strong>Communications</strong> you send us by email, phone, or
                any form on this site.
              </li>
              <li>
                <strong>Basic technical information</strong> automatically
                collected by our website, such as browser type, device, and
                pages visited. If we enable analytics (for example, Google
                Analytics), this may include aggregated usage data. We do not
                combine this data with your identity without your consent.
              </li>
            </ul>
            <p>
              Clinical health information collected during in-person visits is
              handled under our separate Patient Privacy Notice, in accordance
              with Ontario&rsquo;s Personal Health Information Protection Act
              (PHIPA).
            </p>
          </Reveal>

          <Reveal delay={120}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">3. How we use your information</h2>
            <p>We use the information you provide to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-umber/85">
              <li>Respond to your inquiry or callback request.</li>
              <li>Schedule, confirm, or reschedule appointments.</li>
              <li>Send occasional updates about the practice, only if you have opted in.</li>
              <li>Improve the website and the quality of our care.</li>
              <li>Meet our legal and professional obligations.</li>
            </ul>
            <p>We do not sell your personal information to anyone, ever.</p>
          </Reveal>

          <Reveal delay={140}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">4. Third parties we work with</h2>
            <p>
              To operate the practice we rely on a small number of trusted
              service providers, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-umber/85">
              <li>
                <strong>ClearDent</strong>, our appointment booking and
                practice management platform.
              </li>
              <li>
                <strong>Google</strong>, for maps, our Business Profile
                listing, and (once enabled) Analytics and Search Console.
              </li>
              <li>
                <strong>Email and hosting providers</strong> that help us send
                notifications, confirmations, and keep the website running.
              </li>
            </ul>
            <p>
              Each of these providers has its own privacy practices. We only
              share what is necessary to deliver the service you have
              requested.
            </p>
          </Reveal>

          <Reveal delay={160}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">5. Cookies and analytics</h2>
            <p>
              This website may use cookies and similar technologies to remember
              your preferences and understand how visitors use the site. You
              can adjust your browser settings to refuse cookies. If we enable
              analytics that use cookies, you will see a notice giving you a
              choice.
            </p>
          </Reveal>

          <Reveal delay={180}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">6. How we protect your information</h2>
            <p>
              We use reasonable administrative, physical, and technical
              safeguards to protect the personal information you share with us,
              including secure transmission, access controls, and staff
              training. No system is perfectly secure, but we treat your
              information with the same care we treat you.
            </p>
          </Reveal>

          <Reveal delay={200}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">7. Your choices and rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-umber/85">
              <li>Ask what personal information we hold about you.</li>
              <li>Ask us to correct information that is inaccurate.</li>
              <li>Ask us to delete information we no longer need to keep.</li>
              <li>Withdraw your consent to receiving communications from us at any time.</li>
              <li>File a complaint with the Office of the Privacy Commissioner of Canada.</li>
            </ul>
          </Reveal>

          <Reveal delay={220}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">8. Retention</h2>
            <p>
              We keep contact and appointment information only for as long as
              we need it to serve you, respond to your inquiry, or meet a legal
              requirement, and then we securely delete or anonymize it.
            </p>
          </Reveal>

          <Reveal delay={240}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">9. Children</h2>
            <p>
              Our website is not intended for children to use on their own. We
              do not knowingly collect information directly from children under
              the age of 13 without a parent or guardian&rsquo;s involvement.
            </p>
          </Reveal>

          <Reveal delay={260}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">10. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. When we do, we will
              update the &ldquo;Last updated&rdquo; date at the top of the page.
            </p>
          </Reveal>

          <Reveal delay={280}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">11. How to reach us</h2>
            <p>
              If you have any questions about this Privacy Policy or your
              personal information, please contact us:
            </p>
            <ul className="list-none pl-0 space-y-1 mb-6 text-umber/85">
              <li>By email: <a className="text-forest underline underline-offset-4" href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></li>
              <li>By phone: <a className="text-forest underline underline-offset-4" href={`tel:${CONTACT.phoneTel}`}>{CONTACT.phoneLabel}</a></li>
              <li>By mail: Alae Dental, {CONTACT.address}</li>
            </ul>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-16 pt-8 border-t border-umber/20">
              <Link
                to="/"
                data-testid={LEGAL.returnHome}
                className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-forest"
              >
                Return home
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
